import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  type Consignment,
  type ConsignmentPayload,
  useCreateConsignmentMutation,
  useUpdateConsignmentMutation,
} from '../../store/api/consignmentApi'
import {
  type LookupItem,
  useGetLookupCertificationsQuery,
  useLazyGetLookupClientsQuery,
  useLazyGetLookupPackagesQuery,
  useLazyGetLookupQualitiesQuery,
} from '../../store/api/saleContractApi'
import DateTextField from '../../components/DateTextField'
import { usePagePermission } from '../../hooks/usePagePermission'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../../theme/responsive'

const fmtNumStr = (s: string): string => {
  if (!s) return ''
  const neg = s.startsWith('-')
  const abs = neg ? s.slice(1) : s
  const [intPart, ...rest] = abs.split('.')
  const intFormatted = intPart ? Number(intPart).toLocaleString('en-US') : '0'
  const result = rest.length > 0 ? `${intFormatted}.${rest.join('.')}` : intFormatted
  return neg ? `-${result}` : result
}

const numSx = { '& input': { textAlign: 'right' as const } } as const

// Skill `/convert-form` readonly standard: neutral gray thay vì `action.hover`
// (overlay phớt hồng trên theme TLC cream).
const readonlySx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
    cursor: 'default',
  },
} as const

const toNum = (v: string): number | null => {
  if (!v) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

interface ConsignmentFormModel {
  contractNo: string
  contractDate: string
  clientCode: string
  clientName: string
  quantityKg: string
  packagingCode: string
  packaging: string
  qualityCode: string
  certification: string
  lastFixingDate: string
}

const today = () => new Date().toISOString().slice(0, 10)

const emptyForm = (): ConsignmentFormModel => ({
  contractNo: '',
  contractDate: today(),
  clientCode: '',
  clientName: '',
  quantityKg: '' as string,
  packagingCode: '',
  packaging: '',
  qualityCode: '',
  certification: '',
  lastFixingDate: '',
})

const consignmentToForm = (c: Consignment): ConsignmentFormModel => ({
  contractNo: c.contractNo,
  contractDate: c.contractDate ?? '',
  clientCode: c.clientCode,
  clientName: c.clientName ?? '',
  quantityKg: c.quantityKg != null ? String(c.quantityKg) : '',
  packagingCode: c.packagingCode ?? '',
  packaging: c.packaging ?? c.packagingCode ?? '',
  qualityCode: c.qualityCode,
  certification: c.certification,
  lastFixingDate: c.lastFixingDate ?? '',
})

const formToPayload = (f: ConsignmentFormModel): ConsignmentPayload => ({
  contractNo: f.contractNo.trim(),
  contractDate: f.contractDate,
  clientCode: f.clientCode.trim(),
  quantityKg: toNum(f.quantityKg) ?? 0,
  packagingCode: f.packagingCode || null,
  packaging: f.packaging || null,
  qualityCode: f.qualityCode.trim(),
  certification: f.certification.trim(),
  lastFixingDate: f.lastFixingDate,
})

interface ConsignmentFormDialogProps {
  open: boolean
  onClose: () => void
  mode: 'new' | 'edit'
  consignment?: Consignment | null
}

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object' || !('data' in error)) return 'Failed to save consignment.'
  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save consignment.'
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string') return msg
    }
    return JSON.stringify(first)
  }
  return 'Failed to save consignment.'
}

export default function ConsignmentFormDialog({ open, onClose, mode, consignment }: ConsignmentFormDialogProps) {
  const { canEdit } = usePagePermission()
  const [form, setForm] = useState<ConsignmentFormModel>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [createConsignment, { isLoading: isCreating }] = useCreateConsignmentMutation()
  const [updateConsignment, { isLoading: isUpdating }] = useUpdateConsignmentMutation()

  const [fetchClients, { data: clientOpts, isFetching: clientsLoading }] = useLazyGetLookupClientsQuery()
  const [fetchPackages, { data: packageOpts, isFetching: packagesLoading }] = useLazyGetLookupPackagesQuery()
  const [fetchQualities, { data: qualityOpts, isFetching: qualitiesLoading }] = useLazyGetLookupQualitiesQuery()
  const { data: certifications } = useGetLookupCertificationsQuery(undefined, { skip: !open })

  useEffect(() => {
    if (!open) return
    setValidationError(null)
    setFieldErrors({})
    setForm(mode === 'edit' && consignment ? consignmentToForm(consignment) : emptyForm())
    fetchClients()
    fetchPackages()
    fetchQualities()
  }, [consignment, fetchClients, fetchPackages, fetchQualities, mode, open])

  const clientItems = useMemo(() => clientOpts?.items ?? [], [clientOpts])
  const packageItems = useMemo(() => packageOpts?.items ?? [], [packageOpts])
  const qualityItems = useMemo(() => qualityOpts?.items ?? [], [qualityOpts])
  const certItems = useMemo(() => certifications?.items ?? [], [certifications])

  const clearFieldError = useCallback((key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const set = useCallback(
    <K extends keyof ConsignmentFormModel>(key: K, value: ConsignmentFormModel[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }))
      clearFieldError(key as string)
    },
    [clearFieldError],
  )

  const validateLikeAccess = (): { message: string | null; errors: Record<string, string> } => {
    const errors: Record<string, string> = {}
    const messages: string[] = []
    const addError = (k: string, m: string) => {
      if (!errors[k]) {
        errors[k] = m
        messages.push(m)
      }
    }
    if (!form.contractNo.trim()) addError('contractNo', 'Please type Consignment No!')
    if (!form.contractDate) addError('contractDate', 'Please type Consignment Date!')
    if (!form.clientCode.trim()) addError('clientCode', 'Please choose Client!')
    if (form.quantityKg === '') addError('quantityKg', 'Please type Quantity!')
    if (!form.certification.trim()) addError('certification', 'Please choose Certification!')
    if (!form.qualityCode.trim()) addError('qualityCode', 'Please choose Quality!')
    if (!form.packaging.trim() && !form.packagingCode.trim()) addError('packaging', 'Please choose Package!')
    if (!form.lastFixingDate) addError('lastFixingDate', 'Please choose Last fixing date!')
    return { message: messages[0] ?? null, errors }
  }

  const handleSave = async () => {
    const { message, errors } = validateLikeAccess()
    setFieldErrors(errors)
    if (message || Object.keys(errors).length > 0) {
      setValidationError(message ?? 'Please fix the highlighted fields.')
      return
    }
    setValidationError(null)
    const body = formToPayload(form)
    try {
      if (mode === 'edit' && consignment) {
        await updateConsignment({ contractNo: consignment.contractNo, body }).unwrap()
      } else {
        await createConsignment(body).unwrap()
      }
      onClose()
    } catch (e) {
      setValidationError(extractApiErrorMessage(e))
    }
  }

  const isBusy = isCreating || isUpdating

  const renderAutocomplete = (
    label: string,
    valueCode: string,
    valueLabel: string,
    options: LookupItem[],
    loading: boolean,
    tabIndex: number,
    onSearch: (q: string) => void,
    onSelect: (item: LookupItem | null) => void,
    errorKey?: string,
  ) => {
    const selectedOption: LookupItem | null = valueCode
      ? options.find((o) => o.value === valueCode) ?? { value: valueCode, label: valueLabel || valueCode }
      : null
    const errMsg = errorKey ? fieldErrors[errorKey] : undefined
    return (
      <Autocomplete
        size="small"
        options={options}
        loading={loading}
        value={selectedOption}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        filterOptions={(x) => x}
        onInputChange={(_, v, reason) => {
          if (reason === 'input') onSearch(v)
        }}
        onChange={(_, v) => onSelect(v)}
        clearOnBlur
        handleHomeEndKeys
        autoHighlight
        selectOnFocus
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={!!errMsg}
            helperText={errMsg}
            inputProps={{ ...params.inputProps, tabIndex }}
          />
        )}
      />
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          component="span"
          noWrap
          title="Sales Contract - Consignment"
          sx={{ ...TEXT_TRUNCATE, flex: 1, fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            Sales Contract -{' '}
          </Box>
          Consignment
        </Typography>
        <Button
          variant="contained"
          size="small"
          color="success"
          onClick={handleSave}
          disabled={isBusy || !canEdit}
          sx={{ minHeight: 28, py: 0.25, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
        >
          {isBusy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          sx={{ minHeight: 28, py: 0.25, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
        >
          Cancel
        </Button>
      </DialogTitle>
      <Divider />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
          '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
          '& .MuiInputLabel-shrink': { fontWeight: 600 },
          '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', fontWeight: 500 },
          '& .MuiCheckbox-root': { p: 0.5 },
          '& .MuiSvgIcon-root': { fontSize: '1rem' },
          '& .MuiTypography-body2': { fontSize: '0.8125rem' },
          '& .MuiSelect-select': { fontSize: '0.8125rem' },
        }}
      >
        {validationError && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {validationError}
          </Typography>
        )}

        <Box sx={{ maxWidth: 620, mx: 'auto', pb: 1 }}>
          <Grid container spacing={0.9}>
            <Grid item xs={12}>
              <TextField
                label="Consignment  Code"
                size="small"
                fullWidth
                value={form.contractNo}
                onChange={(e) => set('contractNo', e.target.value)}
                InputProps={{ readOnly: mode === 'edit' }}
                sx={mode === 'edit' ? readonlySx : undefined}
                error={!!fieldErrors.contractNo}
                helperText={fieldErrors.contractNo}
              />
            </Grid>
            <Grid item xs={12}>
              <DateTextField
                label="Consignment Date"
                size="small"
                fullWidth
                value={form.contractDate}
                onChange={(value) => set('contractDate', value)}
                nativeInputProps={{ tabIndex: 1 }}
                error={!!fieldErrors.contractDate}
                helperText={fieldErrors.contractDate}
              />
            </Grid>
            <Grid item xs={12}>
              {renderAutocomplete(
                'Client Ref',
                form.clientCode,
                form.clientName,
                clientItems,
                clientsLoading,
                2,
                (q) => fetchClients(q),
                (item) => {
                  set('clientCode', item?.value ?? '')
                  set('clientName', item?.label ?? '')
                },
                'clientCode',
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Quantity"
                size="small"
                fullWidth
                value={fmtNumStr(form.quantityKg)}
                onChange={(e) => set('quantityKg', e.target.value.replace(/,/g, ''))}
                inputProps={{ tabIndex: 3, inputMode: 'decimal' }}
                sx={numSx}
                error={!!fieldErrors.quantityKg}
                helperText={fieldErrors.quantityKg}
              />
            </Grid>
            <Grid item xs={12}>
              {renderAutocomplete(
                'Certification',
                form.certification,
                form.certification,
                certItems,
                false,
                4,
                () => {},
                (item) => set('certification', item?.value ?? ''),
                'certification',
              )}
            </Grid>
            <Grid item xs={12}>
              {renderAutocomplete(
                'Quality',
                form.qualityCode,
                form.qualityCode,
                qualityItems,
                qualitiesLoading,
                5,
                (q) => fetchQualities(q),
                (item) => set('qualityCode', item?.value ?? ''),
                'qualityCode',
              )}
            </Grid>
            <Grid item xs={12}>
              {renderAutocomplete(
                'Package',
                form.packagingCode,
                form.packaging,
                packageItems,
                packagesLoading,
                6,
                (q) => fetchPackages(q),
                (item) => {
                  const packagingCode = typeof item?.meta?.packaging_code === 'string' ? item.meta.packaging_code : item?.value
                  set('packaging', item?.label ?? '')
                  set('packagingCode', packagingCode ?? '')
                },
                'packaging',
              )}
            </Grid>
            <Grid item xs={12}>
              <DateTextField
                label="Last fixing date"
                size="small"
                fullWidth
                value={form.lastFixingDate}
                onChange={(value) => set('lastFixingDate', value)}
                nativeInputProps={{ tabIndex: 7 }}
                error={!!fieldErrors.lastFixingDate}
                helperText={fieldErrors.lastFixingDate}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
